import * as React from 'react';
import Modal from 'react-modal';

import { withRouter } from 'app/context/Router';

import {
  ComposableTableWithFixedPageHeader,
  getButtonCellsGenerator,
  getDatestampCellGenerator,
  getTextCellGenerator,
  getUserAvatarCellGenerator,
  // @ts-ignore
} from 'components/ComposableTable';

import Link from 'components/Link';
import Row from 'components/Row';
// @ts-ignore
import ScrollLoadZone from 'components/ScrollLoadZone';
// @ts-ignore
import Text from 'components/Text';
import DenyRoleRequestDialog from 'roleRequests/components/DenyRoleRequestDialog';

// @ts-ignore
import { ColumnHeader } from 'components/ComposableTable';
// @ts-ignore
import { ReachabilityInfo } from 'components/ReachabilityBadge';
// @ts-ignore
import { RouterState } from 'lib/ReactRouterTypes';

type UserInfo = {
  id: number;
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profilePictureUrl: string | null;
  initials: string | null;
  color: string | null;
  reachabilityInfo: ReachabilityInfo;
};

export type RoleRequest = {
  uuid: string;
  user: UserInfo;
  createdAt: string;
};

type Props = {
  fetchMore: () => Promise<any>;
  hasNextPage: boolean;
  loading: boolean;
  userRole: string;
  roleRequests: RoleRequest[];
  onApproval: (uuid: string) => void;
  onDenial: () => void;
  onPersonClicked: (roleRequest: RoleRequest) => void;
  openUserId: number | null;
  organizationUuid: string;
  router: RouterState;
  pageHeader: React.ReactElement<any>;
  pageHeaderHeight: number;
};

type State = {
  denyModalRoleRequest: RoleRequest | null;
};

class PendingRolesTable extends React.Component<Props, State> {
  state = { denyModalRoleRequest: null };

  showDenyRoleDialog = (uuid: string) => {
    const { roleRequests } = this.props;
    const roleRequest = roleRequests.find(r => r.uuid === uuid);

    if (roleRequest) {
      this.setState({ denyModalRoleRequest: roleRequest });
    }
  };

  hideDenyRoleDialog = () => {
    this.setState({ denyModalRoleRequest: null });
  };

  handleRequestDenied = () => {
    this.props.onDenial();
    this.hideDenyRoleDialog();
  };

  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap: { [name: string]: ColumnHeader } = {
      avatar: {
        name: '',
        type: 'avatar',
      },
      actions: {
        name: 'Actions',
        icon: {
          iconName: 'question-circle',
          tooltip:
            'Approving a request will assign this person the role of teacher. Otherwise, they will have no role.',
        },
      },
      email: {
        name: 'Email',
        width: 300,
      },
      name: { name: 'Name' },
      requestSent: { name: 'Request sent' },
    };

    return columnMap[columnName];
  };

  columnHeaderNames = ['avatar', 'name', 'email', 'requestSent', 'actions'];

  columnHeaders = (): Array<ColumnHeader> => {
    return this.columnHeaderNames.map<ColumnHeader>((name: string) =>
      this.columnHeaderForColumn(name)
    );
  };

  columnGenerators = (): Array<Function> => {
    const { onApproval } = this.props;

    const components: { [name: string]: (data: RoleRequest) => React.ReactElement } = {
      avatar: getUserAvatarCellGenerator({ userField: 'user' }),
      actions: getButtonCellsGenerator([
        {
          fieldForAction: 'uuid',
          buttonLabel: 'Approve',
          onClickAction: onApproval,
          buttonProps: {
            level: 'primary',
            className: 'PendingRoles-approveButton',
          },
        },
        {
          fieldForAction: 'uuid',
          buttonLabel: 'Deny',
          onClickAction: this.showDenyRoleDialog,
          buttonProps: {
            level: 'secondary',
            className: 'PendingRoles-denyButton',
          },
        },
      ]),
      email: getTextCellGenerator((row: RoleRequest) => row.user.email),
      name: getTextCellGenerator((row: RoleRequest) =>
        [row.user.firstName, row.user.lastName].join(' ')
      ),
      requestSent: getDatestampCellGenerator('createdAt'),
    };

    return this.columnHeaderNames.map<Function>((name: string) => components[name]);
  };

  renderNoResultsView = () => {
    const { loading, router } = this.props;

    if (loading) {
      return null;
    }

    return (
      <div data-test="no-results-view">
        <Text align="center" size="medium">
          There are currently no pending requests.{' '}
          <Link to={router.location.pathname.replace('/pending_roles', '?role=teacher')}>
            See the teachers at your school
          </Link>
        </Text>
      </div>
    );
  };

  render() {
    const {
      fetchMore,
      hasNextPage,
      loading,
      onPersonClicked,
      openUserId,
      organizationUuid,
      roleRequests,
      userRole,
      pageHeader,
      pageHeaderHeight,
    } = this.props;

    const { denyModalRoleRequest } = this.state;

    return (
      <div className="PendingRoles-table">
        <Row size="large">
          <ComposableTableWithFixedPageHeader
            columnGenerators={this.columnGenerators()}
            columnHeaders={this.columnHeaders()}
            fixedHeader
            keyField={(row: RoleRequest) => row.user.id}
            loading={loading}
            handleRowClick={onPersonClicked}
            keysOfActiveRows={openUserId ? [openUserId] : []}
            rows={roleRequests}
            className="PendingRolesTable"
            pageHeaderHeight={pageHeaderHeight}
            pageHeader={pageHeader}
          />
        </Row>
        {!loading && hasNextPage && <ScrollLoadZone threshold={500} onZoneActivated={fetchMore} />}
        {roleRequests && roleRequests.length === 0 && this.renderNoResultsView()}
        <Modal isOpen={!!denyModalRoleRequest}>
          <DenyRoleRequestDialog
            organizationUuid={organizationUuid}
            roleRequest={denyModalRoleRequest}
            userRole={userRole}
            onDenial={this.handleRequestDenied}
            onRequestHide={this.hideDenyRoleDialog}
          />
        </Modal>
      </div>
    );
  }
}

export default withRouter(PendingRolesTable);
